import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { Geolocation as CapGeolocation } from "@capacitor/geolocation";

const LocationCtx = createContext(null);

// Free reverse-geocoding via OpenStreetMap Nominatim (no API key required).
async function reverseGeocode(lat, lng) {
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=17&addressdetails=1`;
    const r = await fetch(url, { headers: { "Accept-Language": document.documentElement.lang || "en" } });
    if (!r.ok) return null;
    const data = await r.json();
    return data?.display_name || null;
  } catch { return null; }
}

// Read the visitor's detected country (set by /api/geo/detect + cached in
// localStorage by lib/geo.js). We use it to bias Nominatim results so
// "Taksim Square" in Istanbul isn't beaten by "TAKSIM Square" in Grenoble.
function _cachedCountryCode() {
  try {
    const raw = localStorage.getItem("haulhub_geo");
    if (!raw) return null;
    const g = JSON.parse(raw);
    return (g?.country || "").toLowerCase() || null;
  } catch { return null; }
}

async function searchPlaces(query) {
  if (!query || query.length < 3) return [];
  try {
    const cc = _cachedCountryCode();
    const params = new URLSearchParams({
      format: "json", q: query, limit: "6", addressdetails: "1",
    });
    // Nominatim countrycodes uses ISO 3166-1 alpha-2. We pass ONE to bias
    // but not to hard-restrict — see below fallback for cross-border trips.
    let url = `https://nominatim.openstreetmap.org/search?${params.toString()}${cc ? `&countrycodes=${cc}` : ""}`;
    let r = await fetch(url, { headers: { "Accept-Language": document.documentElement.lang || "en" } });
    if (!r.ok) return [];
    let data = await r.json();
    // Fallback: if the country-biased search finds nothing, retry worldwide.
    if (cc && (!Array.isArray(data) || data.length === 0)) {
      url = `https://nominatim.openstreetmap.org/search?${params.toString()}`;
      r = await fetch(url, { headers: { "Accept-Language": document.documentElement.lang || "en" } });
      if (!r.ok) return [];
      data = await r.json();
    }
    return data.map((p) => ({ label: p.display_name, lat: parseFloat(p.lat), lng: parseFloat(p.lon) }));
  } catch { return []; }
}
export { searchPlaces, reverseGeocode };

export function LocationProvider({ children }) {
  const [loc, setLoc] = useState(() => {
    try {
      const raw = localStorage.getItem("haulhub_loc");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  // status vocabulary (renamed from generic "denied" catch-all so callers
  // can show a specific message instead of blaming the user for a timeout):
  //   idle           → nothing requested yet
  //   requesting     → GPS prompt in flight
  //   granted        → coords received & saved
  //   denied         → user explicitly refused (or system-level block)
  //   unavailable    → device has no geolocation hardware / no capability
  //   timeout        → device did not respond within the timeout window
  //   insecure       → non-HTTPS page (browsers block geolocation)
  //   error          → unknown geolocation failure
  const [status, setStatus] = useState("idle");
  const [errorMessage, setErrorMessage] = useState(null);

  const save = useCallback((next) => {
    setLoc(next);
    if (next) localStorage.setItem("haulhub_loc", JSON.stringify(next));
    else localStorage.removeItem("haulhub_loc");
  }, []);

  const _mapWebError = (err) => {
    // W3C PositionError codes: 1=PERMISSION_DENIED, 2=POSITION_UNAVAILABLE, 3=TIMEOUT.
    const code = err?.code;
    if (code === 1) return "denied";
    if (code === 2) return "unavailable";
    if (code === 3) return "timeout";
    return "error";
  };

  const request = useCallback(async () => {
    // Reject insecure origins loudly. Web geolocation is silently blocked on
    // http:// pages, which caused the confusing "no location" reports on
    // some corporate/Wi-Fi networks. Localhost is a browser-side exception.
    const isSecure = typeof window !== "undefined"
      && (window.isSecureContext || window.location.hostname === "localhost");
    if (!isSecure) {
      setStatus("insecure");
      setErrorMessage("Geolocation requires HTTPS. Open the app on a secure connection.");
      return null;
    }

    // Capacitor native path — uses the OS-level permission prompt on iOS &
    // Android, which is more reliable than the web navigator inside a
    // WebView (which sometimes returns POSITION_UNAVAILABLE silently).
    if (Capacitor?.isNativePlatform?.()) {
      setStatus("requesting");
      setErrorMessage(null);
      try {
        // Ensure permission before calling — iOS refuses to show a prompt
        // if the plist descriptors are missing; we surface that as a
        // structured error instead of a generic "denied".
        const perm = await CapGeolocation.checkPermissions();
        if (perm.location !== "granted") {
          const req = await CapGeolocation.requestPermissions({ permissions: ["location"] });
          if (req.location !== "granted") {
            setStatus("denied");
            setErrorMessage("Location permission is not granted.");
            return null;
          }
        }
        const pos = await CapGeolocation.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 30000,
        });
        const lat = pos.coords.latitude, lng = pos.coords.longitude;
        const label = await reverseGeocode(lat, lng);
        const next = { lat, lng, label, ts: Date.now(), source: "capacitor" };
        save(next);
        setStatus("granted");
        return next;
      } catch (e) {
        // Capacitor throws with .message; map to our vocabulary.
        const m = String(e?.message || e || "").toLowerCase();
        const s = m.includes("denied") || m.includes("permission") ? "denied"
                : m.includes("timeout") ? "timeout"
                : m.includes("unavailable") || m.includes("disabled") ? "unavailable"
                : "error";
        setStatus(s);
        setErrorMessage(e?.message || "Location request failed.");
        return null;
      }
    }

    // Web fallback — Safari on iPhone (not the Capacitor app), Chrome,
    // Firefox, etc. Same status vocabulary as the native path.
    if (!("geolocation" in navigator)) {
      setStatus("unavailable");
      setErrorMessage("This device does not support geolocation.");
      return null;
    }
    setStatus("requesting");
    setErrorMessage(null);
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        async (p) => {
          const lat = p.coords.latitude, lng = p.coords.longitude;
          const label = await reverseGeocode(lat, lng);
          const next = { lat, lng, label, ts: Date.now(), source: "web" };
          save(next);
          setStatus("granted");
          setErrorMessage(null);
          resolve(next);
        },
        (err) => {
          const s = _mapWebError(err);
          setStatus(s);
          setErrorMessage(err?.message || `Geolocation ${s}.`);
          resolve(null);
        },
        // High-accuracy first — the taxi/dispatch flow needs street-level
        // precision. 15s timeout is enough for cold GPS fix on most phones;
        // maximumAge 30s keeps the response snappy without letting stale
        // fixes drift the pickup pin.
        { enableHighAccuracy: true, maximumAge: 30000, timeout: 15000 }
      );
    });
  }, [save]);

  // Auto-refresh label if missing
  useEffect(() => {
    if (loc && !loc.label && loc.lat && loc.lng) {
      reverseGeocode(loc.lat, loc.lng).then((label) => label && save({ ...loc, label }));
    }
  }, [loc, save]);

  return (
    <LocationCtx.Provider value={{ loc, status, errorMessage, request, setLoc: save, searchPlaces }}>
      {children}
    </LocationCtx.Provider>
  );
}

export const useLocation = () => useContext(LocationCtx);
